import React, { useState, useEffect } from 'react';
import {
  FaClipboardList,
  FaSearch,
  FaFileSignature,
  FaCheckCircle,
  FaCertificate,
  FaRedoAlt
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import "./CertificationProcess.css";

const CertificationProcess = () => {
  // ============================================
  // LANGUAGE
  // ============================================
  const [lang, setLang] = useState('en');

  useEffect(() => {
    const saved = localStorage.getItem('lang');

    if (saved && ['en', 'hi', 'mr'].includes(saved)) {
      setLang(saved);
    }
  }, []);

  const changeLang = (language) => {
    setLang(language);
    localStorage.setItem('lang', language);
  };

  // ============================================
  // TRANSLATIONS
  // ============================================
  const t = {
    en: {
      // Page
      title: 'Certification Process',
      titleHindiMarathi: '(ISO Certification Process)',

      subtitle:
        'At CertNexuse, we provide a simple and transparent ISO certification process for Gram Panchayats, private companies, high schools/colleges, small shops, shopkeepers, local businesses and MSMEs. We also create modern responsive websites for small businesses starting from ₹10,000.',

      // Steps
      step1Title: '1️⃣ Application & Enquiry',
      step1Desc:
        'Gram Panchayats, private companies, high schools/colleges, small shopkeepers or businesses select the required ISO standard and submit an application. Our team provides quotation, process details and complete guidance.',

      step2Title: '2️⃣ Documentation Review',
      step2Desc:
        'We review the management system, records and documents of the organization/business. For Gram Panchayats we review GPDP, for schools education processes, for companies HR/production records and for small businesses billing/stock records.',

      step3Title: '3️⃣ Stage 1 Audit (Pre-Assessment)',
      step3Desc:
        'A preliminary assessment is conducted to identify gaps and suggest improvements for the Gram Panchayat, school, company or small business in a simple and practical way.',

      step4Title: '4️⃣ Stage 2 Audit (Final On-Site Assessment)',
      step4Desc:
        'A final on-site audit is conducted at the actual location such as Gram Panchayat office, school/college, company office or shop to verify the management system.',

      step5Title: '5️⃣ Certificate Issuance',
      step5Desc:
        'After successful completion of the audit, the ISO certificate is issued and is generally valid for 3 years. Organizations and businesses can use their ISO certification to build trust and credibility.',

      step6Title: '6️⃣ Surveillance & Recertification',
      step6Desc:
        'Annual surveillance checks and renewal after 3 years help maintain certification. We continue to provide support throughout the certification period.',

      // Certifications
      certificationsTitle: 'Our ISO Certification Services',

      certifications: [
        'ISO 9001:2015 (QMS) – Quality Management System',
        'ISO 14001 (EMS) – Environmental Management System',
        'ISO 45001 / OHSAS 18001 – Occupational Health and Safety Management System',
        'ISO 22000 (FSMS) – Food Safety Management System',
        'HACCP – Hazard Analysis and Critical Control Points',
        'ISO/IEC 27001 (ISMS) – Information Security Management System',
        'ISO 50001 (EnMS) – Energy Management System',
        'ISO 22301 (BCMS) – Business Continuity Management System',
        'ISO 13485 – Medical Devices Quality Management System',
        'IATF 16949 – Automotive Quality Management System',
        'ISO 29990 – Learning Services for Non-Formal Education',
        'CE Marking / GMP / Good Manufacturing Practices',
        'BIFMA LEVEL 1, 2, 3 – Marketing Certificate',
        'GREEN GUARD Certificate',
        'ORGANIC Certificate',
        'EN Certificate',
        'ISI Marking (BIS)'
      ],

      // ISO Service
      isoTitle: 'Request ISO Certification',
      isoDesc:
        'We provide ISO certification services for Gram Panchayats, private companies, high schools, colleges, small shopkeepers and MSMEs.',
      isoDesc2:
        'Complete guidance, documentation preparation and audit support.',
      isoButton: 'Request ISO Certification →',

      // Website Service
      websiteTitle: 'Request a Website for Your Small Business',
      websiteDesc:
        'Along with ISO certification, we create modern responsive websites for small shopkeepers, local service providers and businesses.',
      websiteFeatures:
        'Includes: Home Page, About, Services/Products, Gallery, Contact Form, Google Maps and WhatsApp Button.',
      websitePrice:
        'Price: Starting from ₹10,000 (Domain + Hosting + 1 Year Support).',
      websiteButton: 'Request Website →',

      // ISO Modal
      isoFormTitle: 'ISO Certification Request Form',
      namePlaceholder: 'Your Name *',
      emailPlaceholder: 'Email *',
      phonePlaceholder: 'Phone Number *',

      businessTypeOptions: [
        'Gram Panchayat',
        'Private Company',
        'High School / College',
        'Small Shop / Shopkeeper',
        'Other Business / MSME'
      ],

      isoMessagePlaceholder:
        'What type of ISO certification do you need? (Example: ISO 9001 for Gram Panchayat, ISO 45001 for company...)',

      sendIso: 'Send ISO Request',
      sending: 'Sending...',

      isoSuccess:
        '✓ Your ISO request has been submitted successfully! We will contact you soon.',

      websiteFormTitle: 'Website Request Form',

      websiteTypeOptions: [
        'Gram Panchayat',
        'Small Shop / Shopkeeper',
        'Private Company',
        'High School / College',
        'Other Business'
      ],

      websiteMessagePlaceholder:
        'What type of website do you need? (Example: Gram Panchayat website, shop website, professional company website...)',

      sendWebsite: 'Send Website Request',

      websiteSuccess:
        '✓ Your website request has been submitted successfully! We will contact you soon.',

      error:
        'Something went wrong. Please try again or call: +91-9975797462',

      // Contact
      assistanceTitle: 'Need Assistance?',
      assistanceText:
        'Gram Panchayat, Private Company, School/College or Small Business – Contact us today!',
      email: 'certnexus24@gmail.com',
      phone: '+91-9975797462'
    },

    mr: {
      // Page
      title: 'प्रमाणन प्रक्रिया',
      titleHindiMarathi: '(ISO Certification Process)',

      subtitle:
        'CertNexuse मध्ये आम्ही ग्रामपंचायत, प्रायव्हेट कंपनी, हाय स्कूल/कॉलेज, छोटे दुकानदार, शॉपकीपर्स, लोकल व्यवसाय आणि MSME साठी सोपी व पारदर्शक ISO प्रमाणन प्रक्रिया देतो. तसेच छोट्या व्यवसायांसाठी ₹10,000 पासून आधुनिक responsive वेबसाइट तयार करतो.',

      // Steps
      step1Title: '1️⃣ अर्ज आणि चौकशी (Application & Enquiry)',
      step1Desc:
        'ग्रामपंचायत, प्रायव्हेट कंपनी, हाय स्कूल/कॉलेज, छोटे दुकानदार किंवा व्यवसाय आवश्यक ISO मानक निवडून अर्ज सादर करतात. आमची टीम कोटेशन, प्रक्रिया आणि विशेष मार्गदर्शन देते.',

      step2Title: '2️⃣ दस्तऐवज पुनरावलोकन (Documentation Review)',
      step2Desc:
        'संस्थेच्या किंवा व्यवसायाच्या व्यवस्थापन प्रणाली, रेकॉर्ड्स आणि कागदपत्रांचे परीक्षण केले जाते. ग्रामपंचायतीसाठी GPDP, शाळेसाठी शिक्षण प्रक्रिया, कंपनीसाठी HR/Production आणि छोट्या दुकानासाठी Billing/Stock तपासले जाते.',

      step3Title: '3️⃣ स्टेज 1 ऑडिट (Pre-Assessment)',
      step3Desc:
        'प्राथमिक तपासणी करून संस्थेतील त्रुटी व अंतर ओळखले जातात आणि सुधारणा सुचवल्या जातात. ही प्रक्रिया ग्रामपंचायत, शाळा, कंपनी किंवा छोट्या व्यवसायासाठी सोप्या पद्धतीने केली जाते.',

      step4Title: '4️⃣ स्टेज 2 ऑडिट (Final On-Site Assessment)',
      step4Desc:
        'ग्रामपंचायत कार्यालय, शाळा/कॉलेज, कंपनी ऑफिस किंवा दुकान अशा प्रत्यक्ष साइटवर अंतिम ऑडिट करून व्यवस्थापन प्रणाली तपासली जाते.',

      step5Title: '5️⃣ प्रमाणपत्र जारी करणे (Certificate Issuance)',
      step5Desc:
        'यशस्वी ऑडिटनंतर ISO प्रमाणपत्र जारी केले जाते आणि ते सामान्यतः 3 वर्षांसाठी वैध असते. ISO प्रमाणपत्रामुळे संस्थेला आणि व्यवसायाला विश्वासार्हता व ब्रँडिंग मिळते.',

      step6Title: '6️⃣ सर्व्हेलेन्स आणि पुनर्प्रमाणन (Surveillance & Recertification)',
      step6Desc:
        'दरवर्षी सर्व्हेलेन्स तपासणी आणि 3 वर्षांनंतर पुनर्प्रमाणन केले जाते. संपूर्ण प्रमाणन कालावधीत आम्ही सतत सपोर्ट देतो.',

      // Certifications
      certificationsTitle: 'आमच्या ISO प्रमाणन सेवा',

      certifications: [
        'ISO 9001:2015 (QMS) – गुणवत्ता व्यवस्थापन प्रणाली',
        'ISO 14001 (EMS) – पर्यावरण व्यवस्थापन प्रणाली',
        'ISO 45001 / OHSAS 18001 – व्यावसायिक आरोग्य व सुरक्षा व्यवस्थापन प्रणाली',
        'ISO 22000 (FSMS) – अन्न सुरक्षा व्यवस्थापन प्रणाली',
        'HACCP – Hazard Analysis and Critical Control Points',
        'ISO/IEC 27001 (ISMS) – माहिती सुरक्षा व्यवस्थापन प्रणाली',
        'ISO 50001 (EnMS) – ऊर्जा व्यवस्थापन प्रणाली',
        'ISO 22301 (BCMS) – व्यवसाय सातत्य व्यवस्थापन प्रणाली',
        'ISO 13485 – वैद्यकीय उपकरण गुणवत्ता व्यवस्थापन प्रणाली',
        'IATF 16949 – ऑटोमोटिव्ह गुणवत्ता व्यवस्थापन प्रणाली',
        'ISO 29990 – अनौपचारिक शिक्षणासाठी Learning Services',
        'CE Marking / GMP / Good Manufacturing Practices',
        'BIFMA LEVEL 1, 2, 3 – Marketing Certificate',
        'GREEN GUARD Certificate',
        'ORGANIC Certificate',
        'EN Certificate',
        'ISI Marking (BIS)'
      ],

      // ISO Service
      isoTitle: 'ISO प्रमाणनासाठी रिक्वेस्ट करा',
      isoDesc:
        'ग्रामपंचायत, प्रायव्हेट कंपनी, हाय स्कूल, कॉलेज, छोटे दुकानदार आणि MSME साठी आम्ही ISO प्रमाणन सेवा देतो.',
      isoDesc2:
        'पूर्ण मार्गदर्शन, दस्तऐवज तयारी आणि ऑडिट सपोर्ट.',
      isoButton: 'ISO प्रमाणन रिक्वेस्ट करा →',

      // Website Service
      websiteTitle: 'छोट्या व्यवसायांसाठी वेबसाइट मागवा',
      websiteDesc:
        'ISO प्रमाणनासोबतच छोटे दुकानदार, शॉपकीपर्स, लोकल सर्व्हिस प्रोव्हायडर्स आणि व्यवसायांसाठी आधुनिक responsive वेबसाइट तयार करतो.',
      websiteFeatures:
        'समाविष्ट: Home Page, About, Services/Products, Gallery, Contact Form, Google Maps आणि WhatsApp Button.',
      websitePrice:
        'किंमत: ₹10,000 पासून सुरू (Domain + Hosting + 1 वर्ष Support).',
      websiteButton: 'वेबसाइट मागवा →',

      // ISO Modal
      isoFormTitle: 'ISO प्रमाणन रिक्वेस्ट फॉर्म',
      namePlaceholder: 'तुमचे नाव *',
      emailPlaceholder: 'ईमेल *',
      phonePlaceholder: 'फोन नंबर *',

      businessTypeOptions: [
        'ग्रामपंचायत',
        'प्रायव्हेट कंपनी',
        'हाय स्कूल / कॉलेज',
        'छोटे दुकान / शॉपकीपर',
        'इतर व्यवसाय / MSME'
      ],

      isoMessagePlaceholder:
        'तुम्हाला कोणत्या प्रकारचे ISO प्रमाणन हवे? (उदा. ग्रामपंचायतीसाठी ISO 9001, कंपनीसाठी ISO 45001...)',

      sendIso: 'ISO रिक्वेस्ट पाठवा',
      sending: 'पाठवत आहे...',

      isoSuccess:
        '✓ तुमची ISO रिक्वेस्ट यशस्वीपणे सबमिट झाली आहे! लवकरच आमची टीम तुमच्याशी संपर्क करेल.',

      websiteFormTitle: 'वेबसाइट रिक्वेस्ट फॉर्म',

      websiteTypeOptions: [
        'ग्रामपंचायत',
        'छोटे दुकान / शॉपकीपर',
        'प्रायव्हेट कंपनी',
        'हाय स्कूल / कॉलेज',
        'इतर व्यवसाय'
      ],

      websiteMessagePlaceholder:
        'तुम्हाला कशा प्रकारची वेबसाइट हवी? (उदा. ग्रामपंचायत साइट, दुकान साइट, कंपनी प्रोफेशनल साइट...)',

      sendWebsite: 'वेबसाइट रिक्वेस्ट पाठवा',

      websiteSuccess:
        '✓ तुमची वेबसाइट रिक्वेस्ट यशस्वीपणे सबमिट झाली आहे! लवकरच आमची टीम तुमच्याशी संपर्क करेल.',

      error:
        'काहीतरी चूक झाली. पुन्हा प्रयत्न करा किंवा फोन करा: +91-9975797462',

      // Contact
      assistanceTitle: 'सहाय्य हवे? / Need Assistance?',
      assistanceText:
        'ग्रामपंचायत, प्रायव्हेट कंपनी, शाळा/कॉलेज किंवा छोटा व्यवसाय – आजच आमच्याशी संपर्क साधा!',
      email: 'certnexus24@gmail.com',
      phone: '+91-9975797462'
    },

    hi: {
      // Page
      title: 'प्रमाणन प्रक्रिया',
      titleHindiMarathi: '(ISO Certification Process)',

      subtitle:
        'CertNexuse में हम ग्राम पंचायत, प्राइवेट कंपनी, हाई स्कूल/कॉलेज, छोटे दुकानदार, शॉपकीपर्स, स्थानीय व्यवसाय और MSME के लिए सरल और पारदर्शी ISO प्रमाणन प्रक्रिया प्रदान करते हैं। साथ ही छोटे व्यवसायों के लिए ₹10,000 से आधुनिक responsive वेबसाइट भी बनाते हैं।',

      // Steps
      step1Title: '1️⃣ आवेदन और पूछताछ (Application & Enquiry)',
      step1Desc:
        'ग्राम पंचायत, प्राइवेट कंपनी, हाई स्कूल/कॉलेज, छोटे दुकानदार या व्यवसाय आवश्यक ISO मानक चुनकर आवेदन करते हैं। हमारी टीम quotation, प्रक्रिया और पूरी जानकारी प्रदान करती है।',

      step2Title: '2️⃣ दस्तावेज़ समीक्षा (Documentation Review)',
      step2Desc:
        'संस्था या व्यवसाय की management system, records और documents की समीक्षा की जाती है। ग्राम पंचायत के लिए GPDP, स्कूल के लिए education process, कंपनी के लिए HR/Production और छोटे व्यवसाय के लिए billing/stock records देखे जाते हैं।',

      step3Title: '3️⃣ स्टेज 1 ऑडिट (Pre-Assessment)',
      step3Desc:
        'प्रारंभिक जांच करके कमियों की पहचान की जाती है और सुधार के सुझाव दिए जाते हैं। यह प्रक्रिया ग्राम पंचायत, स्कूल, कंपनी या छोटे व्यवसाय के लिए सरल तरीके से की जाती है।',

      step4Title: '4️⃣ स्टेज 2 ऑडिट (Final On-Site Assessment)',
      step4Desc:
        'ग्राम पंचायत कार्यालय, स्कूल/कॉलेज, कंपनी कार्यालय या दुकान जैसी वास्तविक साइट पर अंतिम ऑडिट किया जाता है और management system की जांच की जाती है।',

      step5Title: '5️⃣ प्रमाणपत्र जारी करना (Certificate Issuance)',
      step5Desc:
        'सफल ऑडिट के बाद ISO प्रमाणपत्र जारी किया जाता है और यह सामान्यतः 3 वर्षों के लिए वैध होता है। ISO certification से संस्था और व्यवसाय को विश्वास एवं विश्वसनीयता मिलती है।',

      step6Title: '6️⃣ सर्विलांस और पुनःप्रमाणन (Surveillance & Recertification)',
      step6Desc:
        'हर वर्ष surveillance check और 3 वर्षों के बाद recertification किया जाता है। पूरी certification अवधि के दौरान हमारी टीम लगातार support प्रदान करती है।',

      // Certifications
      certificationsTitle: 'हमारी ISO प्रमाणन सेवाएं',

      certifications: [
        'ISO 9001:2015 (QMS) – Quality Management System',
        'ISO 14001 (EMS) – Environmental Management System',
        'ISO 45001 / OHSAS 18001 – Occupational Health and Safety Management System',
        'ISO 22000 (FSMS) – Food Safety Management System',
        'HACCP – Hazard Analysis and Critical Control Points',
        'ISO/IEC 27001 (ISMS) – Information Security Management System',
        'ISO 50001 (EnMS) – Energy Management System',
        'ISO 22301 (BCMS) – Business Continuity Management System',
        'ISO 13485 – Medical Devices Quality Management System',
        'IATF 16949 – Automotive Quality Management System',
        'ISO 29990 – Learning Services for Non-Formal Education',
        'CE Marking / GMP / Good Manufacturing Practices',
        'BIFMA LEVEL 1, 2, 3 – Marketing Certificate',
        'GREEN GUARD Certificate',
        'ORGANIC Certificate',
        'EN Certificate',
        'ISI Marking (BIS)'
      ],

      // ISO Service
      isoTitle: 'ISO प्रमाणन के लिए अनुरोध करें',
      isoDesc:
        'हम ग्राम पंचायत, प्राइवेट कंपनी, हाई स्कूल, कॉलेज, छोटे दुकानदार और MSME के लिए ISO प्रमाणन सेवाएं प्रदान करते हैं।',
      isoDesc2:
        'पूरी जानकारी, दस्तावेज़ तैयारी और ऑडिट सपोर्ट।',
      isoButton: 'ISO प्रमाणन अनुरोध करें →',

      // Website Service
      websiteTitle: 'अपने छोटे व्यवसाय के लिए वेबसाइट मांगें',
      websiteDesc:
        'ISO certification के साथ हम छोटे दुकानदारों, local service providers और व्यवसायों के लिए आधुनिक responsive वेबसाइट बनाते हैं।',
      websiteFeatures:
        'शामिल: Home Page, About, Services/Products, Gallery, Contact Form, Google Maps और WhatsApp Button.',
      websitePrice:
        'कीमत: ₹10,000 से शुरू (Domain + Hosting + 1 वर्ष Support).',
      websiteButton: 'वेबसाइट अनुरोध करें →',

      // ISO Modal
      isoFormTitle: 'ISO प्रमाणन अनुरोध फॉर्म',
      namePlaceholder: 'आपका नाम *',
      emailPlaceholder: 'ईमेल *',
      phonePlaceholder: 'फोन नंबर *',

      businessTypeOptions: [
        'ग्राम पंचायत',
        'प्राइवेट कंपनी',
        'हाई स्कूल / कॉलेज',
        'छोटी दुकान / दुकानदार',
        'अन्य व्यवसाय / MSME'
      ],

      isoMessagePlaceholder:
        'आपको किस प्रकार का ISO प्रमाणन चाहिए? (उदाहरण: ग्राम पंचायत के लिए ISO 9001, कंपनी के लिए ISO 45001...)',

      sendIso: 'ISO अनुरोध भेजें',
      sending: 'भेजा जा रहा है...',

      isoSuccess:
        '✓ आपका ISO अनुरोध सफलतापूर्वक भेज दिया गया है! हमारी टीम जल्द ही आपसे संपर्क करेगी।',

      websiteFormTitle: 'वेबसाइट अनुरोध फॉर्म',

      websiteTypeOptions: [
        'ग्राम पंचायत',
        'छोटी दुकान / दुकानदार',
        'प्राइवेट कंपनी',
        'हाई स्कूल / कॉलेज',
        'अन्य व्यवसाय'
      ],

      websiteMessagePlaceholder:
        'आपको किस प्रकार की वेबसाइट चाहिए? (उदाहरण: ग्राम पंचायत वेबसाइट, दुकान वेबसाइट, प्रोफेशनल कंपनी वेबसाइट...)',

      sendWebsite: 'वेबसाइट अनुरोध भेजें',

      websiteSuccess:
        '✓ आपका वेबसाइट अनुरोध सफलतापूर्वक भेज दिया गया है! हमारी टीम जल्द ही आपसे संपर्क करेगी।',

      error:
        'कुछ गलत हो गया। कृपया दोबारा प्रयास करें या कॉल करें: +91-9975797462',

      // Contact
      assistanceTitle: 'सहायता चाहिए? / Need Assistance?',
      assistanceText:
        'ग्राम पंचायत, प्राइवेट कंपनी, स्कूल/कॉलेज या छोटा व्यवसाय – आज ही हमसे संपर्क करें!',
      email: 'certnexus24@gmail.com',
      phone: '+91-9975797462'
    }
  };

  // ============================================
  // MODAL STATES
  // ============================================
  const [modalOpenISO, setModalOpenISO] = useState(false);
  const [modalOpenWebsite, setModalOpenWebsite] = useState(false);

  // ============================================
  // FORM DATA
  // ============================================
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: 'Gram Panchayat',
    message: ''
  });

  const [status, setStatus] = useState('');

  // ============================================
  // HANDLE FORM CHANGE
  // ============================================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ============================================
  // HANDLE SUBMIT
  // ============================================
  const handleSubmit = (e, isISO = true) => {
    e.preventDefault();
    setStatus('loading');

    const serviceID = 'service_7o2vscq';
    const templateID = 'template_tw2co8q';
    const publicKey = 'ASvyy0reI1mYzw6hn';

    const emailParams = {
      ...formData,
      request_type: isISO
        ? 'ISO Certification Request'
        : 'Website Request'
    };

    emailjs
      .send(serviceID, templateID, emailParams, publicKey)
      .then((response) => {
        console.log(
          'SUCCESS!',
          response.status,
          response.text
        );

        setStatus('success');

        setTimeout(() => {
          if (isISO) {
            setModalOpenISO(false);
          } else {
            setModalOpenWebsite(false);
          }

          setFormData({
            name: '',
            email: '',
            phone: '',
            type: 'Gram Panchayat',
            message: ''
          });

          setStatus('');
        }, 3000);
      })
      .catch((err) => {
        console.error('FAILED...', err);
        setStatus('error');
      });
  };

  // ============================================
  // STEPS
  // ============================================
  const steps = [
    {
      icon: <FaClipboardList />,
      title: t[lang].step1Title,
      desc: t[lang].step1Desc
    },
    {
      icon: <FaSearch />,
      title: t[lang].step2Title,
      desc: t[lang].step2Desc
    },
    {
      icon: <FaFileSignature />,
      title: t[lang].step3Title,
      desc: t[lang].step3Desc
    },
    {
      icon: <FaCheckCircle />,
      title: t[lang].step4Title,
      desc: t[lang].step4Desc
    },
    {
      icon: <FaCertificate />,
      title: t[lang].step5Title,
      desc: t[lang].step5Desc
    },
    {
      icon: <FaRedoAlt />,
      title: t[lang].step6Title,
      desc: t[lang].step6Desc
    }
  ];

  // ============================================
  // JSX
  // ============================================
  return (
    <div
      style={{
        padding: '2rem 1rem',
        maxWidth: '1400px',
        margin: '0 auto',
        background:
          'linear-gradient(to bottom, #f8f9ff, #ffffff)',
        minHeight: '100vh'
      }}
    >

      {/* ==========================================
          LANGUAGE SWITCHER
      =========================================== */}
      {/* <div
        className="lang-switcher"
        style={{  
          gap: '8px',
          marginBottom: '2rem'
        }}
      >
        <button
          onClick={() => changeLang('en')}
          className={lang === 'en' ? 'active' : ''}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border:
              lang === 'en'
                ? '2px solid #007bff'
                : '1px solid #ccc',
            background:
              lang === 'en' ? '#007bff' : 'white',
            color:
              lang === 'en' ? 'white' : '#333',
            cursor: 'pointer',
          }}
        >
          English
        </button>

        <button
          onClick={() => changeLang('hi')}
          className={lang === 'hi' ? 'active' : ''}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border:
              lang === 'hi'
                ? '2px solid #007bff'
                : '1px solid #ccc',
            background:
              lang === 'hi' ? '#007bff' : 'white',
            color:
              lang === 'hi' ? 'white' : '#333',
            cursor: 'pointer',
          }}
        >
          हिंदी
        </button>

        <button
          onClick={() => changeLang('mr')}
          className={lang === 'mr' ? 'active' : ''}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            border:
              lang === 'mr'
                ? '2px solid #007bff'
                : '1px solid #ccc',
            background:
              lang === 'mr' ? '#007bff' : 'white',
            color:
              lang === 'mr' ? 'white' : '#333',
            cursor: 'pointer',
          }}
        >
          मराठी
        </button>
      </div> */}

      <section id="certification-process">

        {/* ==========================================
            PAGE TITLE
        =========================================== */}
        <h1
          style={{
            textAlign: 'center',
            color: '#0A1D56',
            fontSize: '2.8rem',
            marginBottom: '1rem'
          }}
        >
          {t[lang].title}{' '}
          <span>{t[lang].titleHindiMarathi}</span>
        </h1>

        <p
          style={{
            textAlign: 'center',
            fontSize: '1.3rem',
            color: '#4a5568',
            marginBottom: '3rem',
            lineHeight: '1.7'
          }}
        >
          {t[lang].subtitle}
        </p>

        {/* ==========================================
            STEPS GRID
        =========================================== */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem',
            marginBottom: '5rem'
          }}
        >
          {steps.map((step, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '12px',
                boxShadow:
                  '0 6px 20px rgba(0,0,0,0.1)',
                padding: '2rem',
                textAlign: 'center',
                transition:
                  'transform 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  'translateY(-8px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform =
                  'translateY(0)';
              }}
            >
              <div
                style={{
                  fontSize: '3.5rem',
                  color: '#007bff',
                  marginBottom: '1rem'
                }}
              >
                {step.icon}
              </div>

              <h3
                style={{
                  color: '#0A1D56',
                  marginBottom: '1rem'
                }}
              >
                {step.title}
              </h3>

              <p
                style={{
                  color: '#4a5568',
                  lineHeight: '1.6'
                }}
              >
                {step.desc}
              </p>
            </div>
          ))}
        </div>

        {/* ==========================================
            CERTIFICATIONS LIST
        =========================================== */}
        <div style={{ marginBottom: '5rem' }}>

          <h2
            style={{
              textAlign: 'center',
              color: '#007bff',
              fontSize: '2.5rem',
              marginBottom: '2rem'
            }}
          >
            {t[lang].certificationsTitle}
          </h2>

          <div
            style={{
              background: '#e3f2ff',
              borderRadius: '12px',
              padding: '2rem',
              boxShadow:
                '0 4px 15px rgba(0,0,0,0.08)'
            }}
          >
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}
            >
              {t[lang].certifications.map(
                (cert, idx) => (
                  <li
                    key={idx}
                    style={{
                      fontSize: '1.1rem',
                      marginBottom: '0.8rem',
                      paddingLeft: '1.5rem',
                      position: 'relative',
                      lineHeight: '1.5'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: 0
                      }}
                    >
                      •
                    </span>

                    {cert}
                  </li>
                )
              )}
            </ul>
          </div>
        </div>
        {/* ==========================================
            ISO + WEBSITE SERVICES
        ========================================== */}
        <div
          className="services-two-column-grid"
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(450px, 1fr))',
            gap: '3rem',
            marginBottom: '5rem',
          }}
        >

          {/* ISO SERVICE */}
          <div
            className="service-card iso-service-card"
            style={{
              background: '#e8f4fd',
              borderRadius: '16px',
              padding: '3rem 2rem',
              textAlign: 'center',
              boxShadow:
                '0 8px 25px rgba(0,123,255,0.15)'
            }}
          >
            <h2
              className="service-card-title"
              style={{
                color: '#0A1D56',
                fontSize: '2.4rem',
                marginBottom: '1.5rem'
              }}
            >
              {t[lang].isoTitle}
            </h2>

            <p
              className="service-card-description"
              style={{
                fontSize: '1.2rem',
                marginBottom: '1rem',
                color: '#4a5568',
                lineHeight: '1.6'
              }}
            >
              {t[lang].isoDesc}
              <br />
              {t[lang].isoDesc2}
            </p>

            <button
              className="service-card-button"
              onClick={() => {
                setStatus('');
                setModalOpenISO(true);
              }}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '1.2rem 3rem',
                borderRadius: '50px',
                fontSize: '1.3rem',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginTop: '1rem'
              }}
            >
              {t[lang].isoButton}
            </button>
          </div>

          {/* WEBSITE SERVICE */}
          <div
            className="service-card website-service-card"
            style={{
              background: '#fff3e0',
              borderRadius: '16px',
              padding: '3rem 2rem',
              textAlign: 'center',
              boxShadow:
                '0 8px 25px rgba(211,84,0,0.15)'
            }}
          >
            <h2
              className="service-card-title"
              style={{
                color: '#d35400',
                fontSize: '2.4rem',
                marginBottom: '1.5rem'
              }}
            >
              {t[lang].websiteTitle}
            </h2>

            <p
              className="service-card-description"
              style={{
                fontSize: '1.2rem',
                marginBottom: '1rem',
                color: '#4a5568',
                lineHeight: '1.6'
              }}
            >
              {t[lang].websiteDesc}
              <br />
              {t[lang].websiteFeatures}
              <br />
              {t[lang].websitePrice}
            </p>

            <button
              className="service-card-button"
              onClick={() => {
                setStatus('');
                setModalOpenWebsite(true);
              }}
              style={{
                background: '#d35400',
                color: 'white',
                border: 'none',
                padding: '1.2rem 3rem',
                borderRadius: '50px',
                fontSize: '1.3rem',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginTop: '1rem'
              }}
            >
              {t[lang].websiteButton}
            </button>
          </div>

        </div>

        {/* ==========================================
            ISO MODAL
        =========================================== */}
        {modalOpenISO && (
          <div
            className="service-modal-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor:
                'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
          >
            <div
              className="service-modal"
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '90%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                boxShadow:
                  '0 10px 30px rgba(0,0,0,0.3)'
              }}
            >

              <button
                onClick={() => {
                  setModalOpenISO(false);
                  setStatus('');
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  fontSize: '2rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#333'
                }}
              >
                ×
              </button>

              <h2
                className="service-modal-title"
                style={{
                  textAlign: 'center',
                  color: '#0A1D56',
                  marginBottom: '1.5rem'
                }}
              >
                {t[lang].isoFormTitle}
              </h2>

              <form
                onSubmit={(e) =>
                  handleSubmit(e, true)
                }
              >
                <input
                  className="service-form-control"
                  type="text"
                  name="name"
                  placeholder={t[lang].namePlaceholder}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <input
                  className="service-form-control"
                  type="email"
                  name="email"
                  placeholder={t[lang].emailPlaceholder}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <input
                  className="service-form-control"
                  type="tel"
                  name="phone"
                  placeholder={t[lang].phonePlaceholder}
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <select
                  className="service-form-control"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                >
                  {t[lang].businessTypeOptions.map(
                    (option, index) => (
                      <option
                        key={index}
                        value={option}
                      >
                        {option}
                      </option>
                    )
                  )}
                </select>

                <textarea
                  className="service-form-control"
                  name="message"
                  placeholder={
                    t[lang].isoMessagePlaceholder
                  }
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  className="service-submit-button"
                  type="submit"
                  disabled={
                    status === 'loading'
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '1rem',
                    backgroundColor:
                      status === 'loading'
                        ? '#ccc'
                        : '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    cursor:
                      status === 'loading'
                        ? 'not-allowed'
                        : 'pointer'
                  }}
                >
                  {status === 'loading'
                    ? t[lang].sending
                    : t[lang].sendIso}
                </button>
              </form>

              {status === 'success' && (
                <p
                  style={{
                    color: 'green',
                    textAlign: 'center',
                    marginTop: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {t[lang].isoSuccess}
                </p>
              )}

              {status === 'error' && (
                <p
                  style={{
                    color: 'red',
                    textAlign: 'center',
                    marginTop: '1rem'
                  }}
                >
                  {t[lang].error}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ==========================================
            WEBSITE MODAL
        =========================================== */}
        {modalOpenWebsite && (
          <div
            className="service-modal-overlay"
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor:
                'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem'
            }}
          >
            <div
              className="service-modal"
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '2rem',
                width: '90%',
                maxWidth: '500px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                boxShadow:
                  '0 10px 30px rgba(0,0,0,0.3)'
              }}
            >

              <button
                onClick={() => {
                  setModalOpenWebsite(false);
                  setStatus('');
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  fontSize: '2rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#333'
                }}
              >
                ×
              </button>

              <h2
                className="service-modal-title"
                style={{
                  textAlign: 'center',
                  color: '#0A1D56',
                  marginBottom: '1.5rem'
                }}
              >
                {t[lang].websiteFormTitle}
              </h2>

              <form
                onSubmit={(e) =>
                  handleSubmit(e, false)
                }
              >
                <input
                  className="service-form-control"
                  type="text"
                  name="name"
                  placeholder={t[lang].namePlaceholder}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <input
                  className="service-form-control"
                  type="email"
                  name="email"
                  placeholder={t[lang].emailPlaceholder}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <input
                  className="service-form-control"
                  type="tel"
                  name="phone"
                  placeholder={t[lang].phonePlaceholder}
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <select
                  className="service-form-control"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                >
                  {t[lang].websiteTypeOptions.map(
                    (option, index) => (
                      <option
                        key={index}
                        value={option}
                      >
                        {option}
                      </option>
                    )
                  )}
                </select>

                <textarea
                  className="service-form-control"
                  name="message"
                  placeholder={
                    t[lang].websiteMessagePlaceholder
                  }
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    margin: '8px 0',
                    borderRadius: '6px',
                    border:
                      '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  className="service-submit-button"
                  type="submit"
                  disabled={
                    status === 'loading'
                  }
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginTop: '1rem',
                    backgroundColor:
                      status === 'loading'
                        ? '#ccc'
                        : '#d35400',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    cursor:
                      status === 'loading'
                        ? 'not-allowed'
                        : 'pointer'
                  }}
                >
                  {status === 'loading'
                    ? t[lang].sending
                    : t[lang].sendWebsite}
                </button>
              </form>

              {status === 'success' && (
                <p
                  style={{
                    color: 'green',
                    textAlign: 'center',
                    marginTop: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  {t[lang].websiteSuccess}
                </p>
              )}

              {status === 'error' && (
                <p
                  style={{
                    color: 'red',
                    textAlign: 'center',
                    marginTop: '1rem'
                  }}
                >
                  {t[lang].error}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ==========================================
            CONTACT SECTION
        =========================================== */}
        <div
          style={{
            textAlign: 'center',
            marginTop: '4rem'
          }}
        >
          <h2
            style={{
              color: '#007bff',
              marginBottom: '1.5rem'
            }}
          >
            {t[lang].assistanceTitle}
          </h2>

          <p
            style={{
              fontSize: '1.4rem',
              margin: '1rem 0',
              fontWeight: 'bold',
              lineHeight: '1.6'
            }}
          >
            {t[lang].assistanceText}
          </p>

          <p
            style={{
              fontSize: '1.5rem',
              lineHeight: '1.8'
            }}
          >
            📧 {t[lang].email}
            <br />
            ☎️ {t[lang].phone}
          </p>
        </div>

      </section>
    </div>
  );
};

export default CertificationProcess;